/**
 * Organization hooks — TanStack Query.
 * Query key factory + optimistic mutations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { organizationService } from '../services/organization.service'
import type {
  Organization,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  InviteMemberInput,
} from '../types/organization.types'

// ─── Query Key Factory ──────────────────────────────────────────────────────

const orgKeys = {
  all: ['organizations'] as const,
  mine: () => [...orgKeys.all, 'mine'] as const,
  detail: (id: string) => [...orgKeys.all, id] as const,
  members: (id: string) => [...orgKeys.all, id, 'members'] as const,
  membership: (id: string) => [...orgKeys.all, id, 'membership'] as const,
  pendingInvites: (id: string) => [...orgKeys.all, id, 'pending-invites'] as const,
  claimable: (id: string) => [...orgKeys.all, id, 'claimable'] as const,
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useMyOrganizations() {
  const { data, ...rest } = useQuery({
    queryKey: orgKeys.mine(),
    queryFn: () => organizationService.getMyOrganizations(),
  })
  return { organizations: data ?? [], ...rest }
}

export function useOrganization(id: string | undefined) {
  const { data, ...rest } = useQuery({
    queryKey: orgKeys.detail(id ?? ''),
    queryFn: () => organizationService.getById(id!),
    enabled: !!id,
  })
  return { organization: data, ...rest }
}

export interface UseOrgMembershipResult {
  /** True solo cuando el servidor ya contestó. No cerrar ninguna puerta mientras sea false. */
  resolved: boolean
  /** Solo significa algo cuando `resolved`. */
  isMember: boolean
  /** Solo significa algo cuando `resolved`. */
  isManager: boolean
  isLoading: boolean
}

/**
 * ¿Pertenezco a esta organización?
 *
 * FALLA ABIERTO, igual que `use-booking-eligibility`. Mientras la respuesta no
 * llegue, `resolved` es false y la pantalla se comporta como antes. Dejarle ver
 * un momento de más a un miembro legítimo es infinitamente mejor que ponerle un
 * muro a alguien que sí pertenece porque su red se puso lenta -- y el servidor
 * responde igual en las dos direcciones.
 *
 * `isSuccess` y no `!isLoading`: una consulta con error también deja de cargar,
 * y tratar un fetch caído como "no sos miembro" es la falla que esto evita.
 */
export function useOrgMembership(orgId: string | undefined): UseOrgMembershipResult {
  const query = useQuery({
    queryKey: orgKeys.membership(orgId ?? ''),
    queryFn: () => organizationService.getMembership(orgId!),
    enabled: !!orgId,
    staleTime: 30_000,
  })

  return {
    resolved: !!orgId && query.isSuccess,
    isMember: query.data?.isMember ?? false,
    isManager: query.data?.isManager ?? false,
    isLoading: query.isLoading,
  }
}

export function useOrgMembers(orgId: string | undefined) {
  const { data, ...rest } = useQuery({
    queryKey: orgKeys.members(orgId ?? ''),
    queryFn: () => organizationService.getMembers(orgId!),
    enabled: !!orgId,
  })
  return { members: data ?? [], ...rest }
}

export function useOrgPendingInvites(orgId: string | undefined) {
  const { data, ...rest } = useQuery({
    queryKey: orgKeys.pendingInvites(orgId ?? ''),
    queryFn: () => organizationService.getPendingInvites(orgId!),
    enabled: !!orgId,
  })
  return { pendingInvites: data ?? [], ...rest }
}

export function useClaimableVehicles(orgId: string | undefined) {
  const { data, ...rest } = useQuery({
    queryKey: orgKeys.claimable(orgId ?? ''),
    queryFn: () => organizationService.findClaimableVehicles(orgId!),
    enabled: !!orgId,
  })
  return { claimableVehicles: data ?? [], ...rest }
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOrganizationInput) => organizationService.create(input),
    onSuccess: (created) => {
      queryClient.setQueryData<Organization[]>(orgKeys.mine(), (current) => {
        const existing = current ?? []
        return [created, ...existing.filter((org) => org.id !== created.id)]
      })
      void queryClient.invalidateQueries({ queryKey: orgKeys.mine() })
    },
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateOrganizationInput }) =>
      organizationService.update(id, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: orgKeys.mine() })
    },
  })
}

export function useInviteMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteMemberInput) => organizationService.inviteMember(input),
    onSuccess: (_data, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.members(organizationId) })
      void queryClient.invalidateQueries({ queryKey: orgKeys.pendingInvites(organizationId) })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId }: { memberId: string; orgId: string }) =>
      organizationService.removeMember(memberId),
    onSuccess: (_data, { orgId }) => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.members(orgId) })
    },
  })
}

export function useClaimVehicle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orgId, vehicleId }: { orgId: string; vehicleId: string }) =>
      organizationService.claimVehicle(orgId, vehicleId),
    onSuccess: (_data, { orgId }) => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.claimable(orgId) })
      void queryClient.invalidateQueries({ queryKey: orgKeys.detail(orgId) })
      void queryClient.invalidateQueries({ queryKey: orgKeys.mine() })
      void queryClient.invalidateQueries({ queryKey: ['host-vehicles'] })
    },
  })
}
